import { useCallback, useEffect, useRef } from 'react';
import { Virtualizer } from '@tanstack/react-virtual';
import { useAdditionalInfos } from '../hooks/use-additional-infos/additional-infos-provider';

export const useUpdateVisibleAsRead = (
    enabled: boolean,
    virtualizer: Virtualizer<HTMLDivElement, Element>,
    // Offset of the chronological `list` inside the virtualized combinedData
    // (pinned rows + divider precede it in DESC; see AdditionalInfoRenderer).
    // Virtual indices address combinedData, so they must be shifted back by
    // this offset before slicing `list` — otherwise pinned rows would cause
    // the wrong rows to be marked as read.
    chronologicalStartIndex = 0
): void => {
    const { list: data, hasUnread, markAsRead } = useAdditionalInfos();

    const running = useRef(false);
    // Set when a trigger lands while a dismissal batch is in flight; the
    // batch's completion re-evaluates against the LATEST inputs (via
    // latestReadInfos). Without this, the run is dropped for good — e.g. when
    // a notification arrives just before its row, the batch started at
    // notification time misses the row, the row-insert trigger is swallowed
    // by the running guard, and the notification is never dismissed.
    const pendingRerun = useRef(false);
    const latestReadInfos = useRef<() => void>(() => {});
    const virtualItems = virtualizer.getVirtualItems();
    const readInfos = useCallback(() => {
        // Don't run if disabled.
        if (!enabled) return;
        // Don't overlap an in-flight batch, but re-evaluate when it finishes.
        if (running.current) {
            pendingRerun.current = true;
            return;
        }
        // Don't run without both unreadInfos and virtualItems
        if (!hasUnread) return;
        if (!Array.isArray(virtualItems) || virtualItems.length === 0) return;
        // Translate the visible virtual range into the chronological range,
        // clamping out pinned rows, the divider, and the loader row.
        const firstListIndex = Math.max(0, virtualItems[0].index - chronologicalStartIndex);
        const lastListIndex = Math.min(
            data.length - 1,
            virtualItems[virtualItems.length - 1].index - chronologicalStartIndex
        );
        // No chronological rows visible (e.g. only the pinned section is on screen).
        if (lastListIndex < firstListIndex) return;
        // Start running, get the visible infos.
        running.current = true;
        const visibleInfos = data.slice(firstListIndex, lastListIndex + 1);
        // Mark them as read, don't block the next run.
        markAsRead(visibleInfos).finally(() => {
            running.current = false;
            if (pendingRerun.current) {
                pendingRerun.current = false;
                // Use the latest callback, not this closure — the visible
                // range and unread state have moved on since this batch began.
                latestReadInfos.current();
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [virtualItems, data, enabled, running, hasUnread, chronologicalStartIndex]);

    useEffect(() => {
        latestReadInfos.current = readInfos;
    });

    useEffect(() => {
        readInfos();
    }, [readInfos]);
};
